//Store 
import useSiteStore from '../../../store/siteStore.js';

// Components
import { NavigateBeforeOutlined, NavigateNextOutlined, PersonOutlined, KeyboardArrowDownRounded } from "@mui/icons-material";
import { Avatar, Box, IconButton, Paper, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import CoachList from "../../custom/CoachList.js";
import CustomFormControl from "../../custom/FormControl.js";
import { CustomSelect, CustomMenuItem, getMenuProps } from "../../custom/Select.js";
import WeeklyScheduleTable from "../../custom/WeeklyScheduleTable.js";

// Style
import { blue, grey, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../../styles/colorSchemes/index.js';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Other
import { getWeek, getConsistentAvatarColor } from '../../../utils/index.js';

const ParentHome = (props) => {
    const { athletes, coaches, onChangeSelectedAthlete, openAndCloseAll, openAndCloseTableRow, selectedAthlete, selectOpen, handleSelectOpen, handleSelectClose } = props;
    const { calendarWeekString, changeCalenderWeek, highlightCurrentCalenderWeek, selectedCalenderWeek, selectedTab, setSelectedCalenderWeek, year, setYear }
        = useSiteStore();
    const { isDarkMode } = useAppTheme();

    // Dark Mode Helper Logic for week highlighting
    const currentDate = new Date();
    const currentWeek = getWeek(currentDate);
    const currentYear = currentDate.getFullYear();
    
    const isFuture = year > currentYear || (year === currentYear && selectedCalenderWeek > currentWeek);
    const isPast = year < currentYear || (year === currentYear && selectedCalenderWeek < currentWeek);
    const isCurrent = year === currentYear && selectedCalenderWeek === currentWeek;

    const convertBufferToBase64String = (athlete) => {
        if (!athlete?.profile?.picture?.data) return '';
        return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
    };

    return (
        <Stack sx={{ justifyContent: 'center', ml: '10px', mr: '10px', alignItems: 'center', minWidth: '0px', width: '1fr' }}>

            {/* Navigation Row - Week Selector (center) and Athlete Selector (right) */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mb: 2,
                mt: 1,
                gap: { xs: 2, md: 0 },
                position: 'relative',
                minHeight: { xs: 'auto', md: '64px' },
            }}>
                {/* Week Selector - Center - Only for Weekly Schedule Tab */}
                {selectedTab === 0 && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: { xs: '100%', md: 'auto' },
                        maxWidth: { xs: '100%', md: 'none' }
                    }}>
                        <Paper
                            elevation={0}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                border: '1px solid',
                                borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                                borderRadius: '24px',
                                overflow: 'hidden',
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'background.paper',
                                transition: 'all 0.2s ease',
                                width: { xs: '100%', md: 'auto' },
                                maxWidth: { xs: '100%', md: 'none' },
                                '&:hover': {
                                    boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                    borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                                }
                            }}
                        >
                            {/* Previous Week Button */}
                            <IconButton
                                onClick={() => changeCalenderWeek(false)}
                                sx={{
                                    borderRadius: 0,
                                    px: { xs: 1, sm: 1.5 },
                                    py: 0.75,
                                    color: isDarkMode 
                                        ? (isFuture ? orange[500] : darkTheme.textSecondary)
                                        : highlightCurrentCalenderWeek(selectedCalenderWeek, "less"),
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
                                        color: isDarkMode ? orange[500] : undefined
                                    }
                                }}
                            >
                                <NavigateBeforeOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>

                            {/* Current Week Display - Click to go to today */}
                            <Box
                                onClick={() => {
                                    const currentDate = new Date();
                                    setYear(currentDate.getFullYear());
                                    setSelectedCalenderWeek(getWeek(currentDate));
                                }}
                                sx={{
                                    px: { xs: 2, sm: 3 },
                                    py: 0.75,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    flex: 1,
                                    minWidth: 0,
                                    '&:hover': {
                                        bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50]
                                    }
                                }}
                            >
                                <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" justifyContent="center">
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{
                                            color: isDarkMode 
                                                ? (isCurrent ? orange[500] : darkTheme.textPrimary)
                                                : highlightCurrentCalenderWeek(selectedCalenderWeek, "equal"),
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        KW {selectedCalenderWeek}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ display: { xs: 'none', sm: 'block' }, color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}
                                    >
                                        •
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: isDarkMode ? darkTheme.textSecondary : 'text.secondary'
                                        }}
                                    >
                                        {calendarWeekString}
                                    </Typography>
                                </Stack>
                        </Box>

                        {/* Next Week Button */}
                        <IconButton
                                onClick={() => changeCalenderWeek(true)}
                                sx={{
                                    borderRadius: 0,
                                    px: { xs: 1, sm: 1.5 },
                                    py: 0.75,
                                    color: isDarkMode 
                                        ? (isPast ? orange[500] : darkTheme.textSecondary)
                                        : highlightCurrentCalenderWeek(selectedCalenderWeek, "more"),
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50],
                                        color: isDarkMode ? orange[500] : undefined
                                    }
                                }}
                            >
                                <NavigateNextOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                        </Paper>
                    </Box>
                )}

                {/* Athlete Selector - Always visible on both tabs */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: { xs: '100%', md: 'auto' },
                    position: { xs: 'static', md: 'absolute' },
                    right: { md: 0 },
                    mr: { xs: 0, md: 3 },
                }}>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                color: isDarkMode ? darkTheme.textSecondary : grey[700],
                                display: { xs: 'none', sm: 'flex' },
                                alignItems: 'center',
                                gap: 0.75
                            }}
                        >
                            <PersonOutlined sx={{ fontSize: 18, color: isDarkMode ? orange[500] : blue[600] }} />
                            Athlet:
                        </Typography>
                        <CustomFormControl sx={{
                            minWidth: { xs: '100%', sm: 240 },
                        }} color={isDarkMode ? orange[500] : blue[500]} isDarkMode={isDarkMode} darkTheme={darkTheme}>
                            <CustomSelect
                                disabled={athletes.length === 0}
                                value={athletes.length === 0 ? '' : (selectedAthlete || '')}
                                onChange={onChangeSelectedAthlete}
                                open={selectOpen}
                                onOpen={handleSelectOpen}
                                onClose={handleSelectClose}
                                IconComponent={KeyboardArrowDownRounded}
                                size="small"
                                displayEmpty
                                isDarkMode={isDarkMode}
                                darkTheme={darkTheme}
                                primaryColor={isDarkMode ? orange[500] : blue[500]}
                                renderValue={(selected) => {
                                    if (athletes.length === 0 || !selected) {
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: isDarkMode ? darkTheme.textSecondary : grey[500] }}>
                                                    Keine Athleten verfügbar
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                src={`data:image/png;base64,${convertBufferToBase64String(selected)}`}
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    bgcolor: getConsistentAvatarColor(selected),
                                                }}
                                            >
                                                {!selected?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 14 }} />}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500} sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                                {selected.profile.lastName}, {selected.profile.firstName}
                                            </Typography>
                                        </Box>
                                    );
                                }}
                                MenuProps={getMenuProps(isDarkMode, darkTheme)}
                            >
                                {athletes.filter(athlete => athlete.profile.lastName !== null)
                                    .sort((a, b) => {
                                        return String(a.profile.lastName).localeCompare(String(b.profile.lastName))
                                    })
                                    .map((athlete, index) => (
                                        <CustomMenuItem
                                            key={index}
                                            value={athlete}
                                            isDarkMode={isDarkMode}
                                            darkTheme={darkTheme}
                                            primaryColor={isDarkMode ? orange[500] : blue[500]}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                <Avatar
                                                    src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: getConsistentAvatarColor(athlete),
                                                    }}
                                                >
                                                    {!athlete?.profile?.picture?.data && <PersonOutlined sx={{ fontSize: 16 }} />}
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={500} noWrap sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                                        {athlete.profile.lastName}, {athlete.profile.firstName}
                                                    </Typography>
                                                    {athlete.squad && (
                                                        <Typography variant="caption" noWrap sx={{ color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                            {athlete.squad}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CustomMenuItem>
                                    ))}
                            </CustomSelect>
                        </CustomFormControl>
                    </Box>
                </Box>

            {(selectedAthlete === null || selectedAthlete === undefined) && (
                <Box sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, sm: 4, md: 6 },
                    py: { xs: 4, sm: 5, md: 6 },
                    minHeight: '400px'
                }}>
                    <Box sx={{
                        width: { xs: 60, sm: 70, md: 80 },
                        height: { xs: 60, sm: 70, md: 80 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <PersonOutlined sx={{ fontSize: { xs: 48, sm: 54, md: 60 }, color: isDarkMode ? darkTheme.textSecondary : grey[400] }} />
                    </Box>
                    <Typography variant='h6' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.25rem' } }}>
                        Kein Athlet ausgewählt
                    </Typography>
                    <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ textAlign: 'center', maxWidth: { xs: 300, sm: 350, md: 400 }, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                        Wähle einen Athleten aus, um den Trainingsplan anzuzeigen.
                    </Typography>
                </Box>
            )}

            {selectedAthlete && (
                <Box sx={{ width: '100%' }}>
                    {selectedTab === 0 && (
                        <Box>
                            <WeeklyScheduleTable
                                isFeedbackEditable={false}
                                isEditable={false}
                                openEditTrainingSession={false}
                                onCloseEditTrainingSession={false}
                                onOpenEditTrainingSession={false}
                                openAndCloseTableRow={openAndCloseTableRow}
                                openAndCloseAll={openAndCloseAll}
                                showFeedbackButton={true}
                                selectedAthlete={selectedAthlete} />
                        </Box>
                    )}

                    {selectedTab === 1 && coaches !== undefined && (
                        <Box sx={{ width: '100%' }}>
                            <CoachList search={""}
                                coaches={coaches}
                                showAthleteAmount={false}
                                isEditable={false} />
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    )
}

export default ParentHome;